import type {
  QuestionnaireItemAnswerOptionWithTranslations,
  QuestionnaireItemWithTranslations,
  QuestionnaireWithTranslations,
} from '@liza/fhir-utils';
import {
  buildScoreObservationTemplate,
  buildTemplateExtractExtension,
  frequencyAnswerOption,
  SEMVER_VERSION_ALGORITHM_EXTENSION,
  symptomItem,
  withEs,
} from './scoredSurvey';

/**
 * Pain Intensity NRS — a single-item, patient-rated 0–10 numeric rating scale of average pain
 * (0 = "No pain" → 10 = "Worst imaginable pain"). Required by CMS ACCESS (MSK track) beneficiaries,
 * where it is accepted as the NRS equivalent of PROMIS NRS v1.0 Pain Intensity 1a; success is
 * submission of the measure, not a score threshold.
 *
 * Single-item instrument: the score LOINC {@link PAIN_INTENSITY_NRS_RESULT_CODE} (72514-3) is used
 * for both the panel (`Questionnaire.code`) and the item, and the selected ordinal (0–10) *is* the
 * score — a one-term `$extract` sum, like {@link ./pgic} and {@link ./moodScale}. Unlike those it
 * has real LOINC codes throughout, so the result code lives on LOINC rather than the minted
 * `survey-scores` system. Behavior is verified by
 * `src/__tests__/liveMedplum/painIntensityNrsExtractScoring.test.ts`.
 *
 * Source: the bilingual Pain Intensity NRS FHIR survey JSON attached to the originating issue
 * (English + Spanish). The answer displays are transcribed from those sources. The item prompt was
 * normalized during clinical review (issue #5484) so both locales carry the same instruction level
 * ("choose a number from 0 to 10") — the source EN omitted it while the ES included it — keeping the
 * PROMIS 7-day recall window. The answer options use real LOINC LA codes (`LA6111-4` … `LA13942-0`)
 * from the LOINC `LL2335-9` answer list.
 */

/** Pain Intensity NRS score. A real total-score LOINC, so it doubles as the panel code (single-item). */
export const PAIN_INTENSITY_NRS_RESULT_CODE = '72514-3';

const PAIN_NRS_DISPLAY = 'Pain severity - 0-10 verbal numeric rating [Score] - Reported';

/**
 * The 0–10 scale options. Only the endpoints carry descriptive text ("No pain" / "Worst imaginable
 * pain"); the interior options display the bare number. Each option's `optionPrefix` is the digit
 * shown on the button and `ordinalValue` is the score it contributes — equal for an NRS, where the
 * chosen number is the score.
 *
 * `frequencyAnswerOption` is named for the 0–3 frequency scales it was built for (PHQ-9/GAD-7), but
 * it is mechanically just prefix + ordinal + LOINC coding + es display, so it fits the NRS too. This
 * is a numeric rating scale, not a frequency scale.
 */
const painAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  frequencyAnswerOption('0', 0, 'LA6111-4', '0 - No pain', '0 - Sin dolor'),
  frequencyAnswerOption('1', 1, 'LA6112-2', '1', '1'),
  frequencyAnswerOption('2', 2, 'LA6113-0', '2', '2'),
  frequencyAnswerOption('3', 3, 'LA6114-8', '3', '3'),
  frequencyAnswerOption('4', 4, 'LA6115-5', '4', '4'),
  frequencyAnswerOption('5', 5, 'LA10137-0', '5', '5'),
  frequencyAnswerOption('6', 6, 'LA10138-8', '6', '6'),
  frequencyAnswerOption('7', 7, 'LA10139-6', '7', '7'),
  frequencyAnswerOption('8', 8, 'LA10140-4', '8', '8'),
  frequencyAnswerOption('9', 9, 'LA10141-2', '9', '9'),
  frequencyAnswerOption('10', 10, 'LA13942-0', '10 - Worst imaginable pain', '10 - El peor dolor imaginable'),
];

const painItem: QuestionnaireItemWithTranslations = symptomItem(
  '/72514-3',
  PAIN_INTENSITY_NRS_RESULT_CODE,
  PAIN_NRS_DISPLAY,
  'In the past 7 days, on average, how would you rate your pain? Please choose a number from 0 to 10.',
  'En los últimos 7 días, en promedio, ¿cómo calificaría su dolor? Escoja un número del 0 al 10.',
  true,
  painAnswerOptions,
);

const painNrsScoreObservationTemplate = buildScoreObservationTemplate({
  id: 'pain-nrs-score-tmpl',
  totalScoreCode: PAIN_INTENSITY_NRS_RESULT_CODE,
  display: 'Pain Intensity NRS',
  scoredItems: [painItem],
});

const URL = 'https://liza.health/fhir/questionnaire/pain-intensity-nrs';

export const painIntensityNrsQuestionnaire: QuestionnaireWithTranslations = {
  resourceType: 'Questionnaire',
  meta: {
    profile: ['http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire'],
  },
  extension: [SEMVER_VERSION_ALGORITHM_EXTENSION, buildTemplateExtractExtension('pain-nrs-score-tmpl')],
  contained: [painNrsScoreObservationTemplate],
  url: URL,
  // No standard OID — mirror the canonical url as a FHIR identifier so re-deploys upsert
  // (identifier-based idempotency) rather than duplicate.
  identifier: [{ system: 'urn:ietf:rfc:3986', value: URL }],
  version: '1',
  name: 'PainIntensityNRS',
  language: 'en',
  title: 'Pain Intensity NRS',
  // The source JSON leaves the title in English; this Spanish title is Liza-provided (not from the
  // validated source) and was approved in clinical review (issue #5484).
  _title: withEs('Escala numérica de intensidad del dolor (NRS)'),
  status: 'active',
  subjectType: ['Patient'],
  date: '2026-06-22',
  publisher: 'Liza Health',
  jurisdiction: [{ coding: [{ system: 'urn:iso:std:iso:3166', code: 'US' }] }],
  copyright:
    'This material contains content from LOINC® (http://loinc.org). LOINC is copyright © 1995-2024, Regenstrief Institute, Inc. and the LOINC Committee, and is available at no cost under the license at http://loinc.org/license. PROMIS® instruments are developed by the PROMIS Health Organization (PHO). © 2008-2017 PROMIS Health Organization. Available for use at no cost under the terms at http://www.healthmeasures.net/images/PROMIS/PROMISResponsibleUse.pdf',
  code: [{ system: 'http://loinc.org', code: PAIN_INTENSITY_NRS_RESULT_CODE, display: PAIN_NRS_DISPLAY }],
  item: [painItem],
};
